import { peSponsorLogos, teamLogoMarquee } from "./logoSets";

export const kanziSections = {
  hero: {
    eyebrow: "PRIVATE AI FOR HEALTHCARE",
    titleAccent: "Kanzi.",
    titleMain: "The Enterprise Brain for Healthcare.",
    description:
      "A private AI with persistent memory that lives inside your organization, " +
      "learns how you actually operate, and runs the work your team shouldn't be " +
      "doing manually. Live in weeks. Data never leaves your walls.",
    primaryCta: { label: "Talk to Us", href: "/#contact" },
    secondaryCta: { label: "See How It Works", href: "/#how-it-works" },
  },

  manifesto: {
    eyebrow: "Kanzi",
    lines: [
      "Private AI with persistent memory",
      "that lives inside your organization,",
      "learns how you actually operate,",
      "and runs the work that matters.",
    ],
    accentWords: ["Private", "AI", "persistent", "memory"],
    cta: { label: "See How It Works", href: "/#how-it-works" },
  },

  scenes: {
    eyebrow: "What Kanzi Actually Does",
    title: "AI Automation in Healthcare — Real Workflows, Real Results.",
    items: [
      {
        number: "01",
        category: "Lead Generation",
        title: "From agency dependency to autonomous growth.",
        narrative: [
          "A multi-site healthcare operator was paying an SEO agency $70,000 a month. In four hours, Kanzi spun up 20,000 programmatic SEO pages across their service lines.",
          "Then it kept going. Without being asked, it cross-referenced Google search trends against the client's EHR data and flagged the gap: \"A meaningful share of your revenue is knee injections — and you're missing the keyword searches that would bring those patients in.\"",
          "That's not vendor replacement. That's an AI doing cross-functional strategic work no human agency could do — because it had access to the Brain.",
        ],
        visual: {
          type: "lead-gen",
          stats: [
            { value: "20,000", label: "Pages generated" },
            { value: "4 hrs", label: "Time to deploy" },
            { value: "$70K/mo", label: "Agency budget replaced" },
          ],
          badge: "Cross-functional insight",
          insight: "Revenue keyword gap identified without being asked.",
        },
      },
      {
        number: "02",
        category: "Operations",
        title: "Visibility the team never had.",
        narrative: [
          "A multi-specialty medical management firm was running its core operations manually — a full team spending their days on payment posting, reconciliation, and chasing exceptions. Visibility into actual performance was limited to whatever the team could pull together in spreadsheets.",
          "Kanzi connected to their bank accounts, ran daily reconciliation automatically, and pushed results back into their systems.",
          "Then it went deeper: surfacing a 30% actionable revenue opportunity the team hadn't been able to see — workflows where revenue was leaking, payers underperforming against contracted rates, and patient pathways with measurable upside.",
        ],
        visual: {
          type: "operations",
          headline: "30%",
          headlineLabel: "Actionable revenue opportunity surfaced",
          rows: [
            { label: "Payment reconciliation", status: "Automated" },
            { label: "Payer performance", status: "Analyzed" },
            { label: "Revenue leakage", status: "Identified" },
            { label: "Patient pathways", status: "Mapped" },
          ],
        },
      },
      {
        number: "03",
        category: "Revenue Cycle",
        title: "Signal where there was silence.",
        narrative: [
          "A specialty provider group was paying for medication that was getting reimbursed at a higher rate by insurance — except, somewhere in the workflow, claims were getting denied. Quietly. At scale. No one knew until the cash gap showed up on the P&L.",
          "Kanzi surfaced the signal: Here is the payer denying your claims. Here is the contracted rate. Here is the list of affected patients. Here is the number to call.",
          "Not a dashboard. Not a report. An actionable instruction the team could execute that day.",
        ],
        visual: {
          type: "rcm",
          badge: "Denial Signal Detected",
          rows: [
            { label: "Payer", value: "Identified" },
            { label: "Contracted Rate", value: "On file" },
            { label: "Affected Patients", value: "Listed" },
            { label: "Action", value: "Call ready" },
          ],
          note: "Not a dashboard. An actionable instruction.",
        },
      },
    ],
  },

  howItWorks: {
    eyebrow: "How It Works",
    title: "A persistent brain. A swarm of agents. A senior team that deploys it.",
    description:
      "Consumer AI tools are getting better at memory — but they remember you, not your organization. Kanzi is the opposite. It has persistent memory of how your business actually runs: your billing patterns, your referral flows, your clinical workflows, your reporting cadences, your institutional logic.",
    agentsNote:
      "Thousands of agents do the work in the background. Your team interacts through chat, dashboards, and structured workflows. Process Partners deploy it alongside your team.",
    modelNote:
      "The foundation models are interchangeable. When the next major model is released, we switch it like a light switch. The Brain keeps running without skipping a beat.",
    pillars: [
      {
        title: "Private by design.",
        description:
          "Kanzi runs on your infrastructure. Patient data, financial records, and operational intelligence stay inside your walls — never trained on, never shared, never leaked.",
        icon: "ShieldCheck",
      },
      {
        title: "Persistent and accumulating.",
        description:
          "Every workflow Kanzi runs makes the Brain smarter. Institutional knowledge that used to live in a few people's heads becomes encoded operational capability that compounds across the organization.",
        icon: "TrendingUp",
      },
      {
        title: "Model-agnostic.",
        description:
          "The intelligence lives in your Brain, not in any vendor's model. When the AI landscape shifts every three months — and it will — your operating layer doesn't shift with it.",
        icon: "Shuffle",
      },
    ],
  },

  approach: {
    eyebrow: "Our Approach",
    title: "We build alongside you.\nThen we stay one click away.",
    description:
      "Kanzi isn't a tool we hand you and walk away. It also isn't a consulting engagement that depends on perpetual dependency. It's a Service as a Software model where the work and the platform arrive together, and our role evolves as your Brain matures.",
    footer: "The Brain stays. Your team operates it. We stay close — one click away when you need us.",
    steps: [
      {
        number: "01",
        title: "Systems Thinking",
        description:
          "Mapping your entire organization as a process — where things flow, where they drain off, and where the feedback loops should be.",
      },
      {
        number: "02",
        title: "Deploy",
        description:
          "Process Partners deploy Kanzi against your real workflows in weeks, not months.",
      },
      {
        number: "03",
        title: "Service",
        description:
          "Your Brain knows your business. Your team operates fluently with it. We stay on for new workflows, exceptions, tuning, and the next thing you didn't know you needed yet.",
      },
    ],
  },

  founder: {
    eyebrow: "From the Founder",
    attribution: "James Card, Founder & CEO",
    bio: "Twenty years in healthcare. Nine-figure exit.",
    paragraphs: [
      "I've spent twenty years in healthcare — building, scaling, exiting. The last platform I architected helped drive a nine-figure acquisition by one of the largest asset managers in the world.",
      "Here's what I learned: most of what passes for \"transformation\" in healthcare is a deck and a roadmap that lives in a SharePoint folder for two years. We don't do that. We deploy a brain inside your organization, we work alongside your team to make it operational, and we move the numbers that matter — revenue, capacity, margin — starting in the first quarter.",
      "Kanzi isn't strategy. It's the work itself, running.",
    ],
    cta: { label: "Talk to Us", href: "/#contact" },
  },

  credibility: {
    eyebrow: "Credibility",
    trustedLabel: "Trusted by",
    teamLabel: "Team Backgrounds",
    teamDescription:
      "The team behind Kanzi has built infrastructure for federal defense agencies, financial systems for top-tier asset managers and global payment networks, and operational platforms across healthcare. The last platform our founder architected helped drive a nine-figure healthcare exit.",
    sponsorLogos: peSponsorLogos,
    teamLogos: teamLogoMarquee,
  },

  audience: {
    eyebrow: "Who It's For",
    title: "Healthcare organizations that need to move faster than their hiring plan allows.",
    segments: [
      {
        badge: "PE & Portfolio",
        title: "Private Equity & Portfolio Companies",
        description:
          "Your value creation plan moves at the speed of your slowest portco. Kanzi changes that math. It deploys in weeks — not the 12-to-18 months it takes to hire and ramp internal teams — surfaces operational value in the first quarter, and produces standardized reporting your sponsors can actually compare across acquisitions. Operating alpha, without waiting on hiring cycles.",
        bullets: [
          "Deploys in weeks, not 12-18 months",
          "Operational value surfaces in Q1",
          "Standardized cross-portfolio reporting",
        ],
      },
      {
        badge: "Independent Operators",
        title: "Independent Healthcare Operators",
        description:
          "You're running a sophisticated business without the corporate overhead to match. Kanzi gives you the operating layer of a much larger company on a platform fee — institutional-grade revenue cycle, operations, and reporting, deployed inside your walls. No CDO hire. No six-figure consulting engagement. No internal data team.",
        bullets: [
          "Enterprise operating layer, platform fee",
          "No CDO hire required",
          "No six-figure consulting engagement",
        ],
      },
    ],
  },

  brainCta: {
    eyebrow: "Ready to deploy",
    title: "Ready to stand up your Brain?",
    description:
      "Available for Q3 2026 deployments. Engagements scoped to organizational complexity. Discovery call is 30 minutes — we'll tell you within that call whether Kanzi is the right fit for your organization.",
    cta: { label: "Talk to Us", href: "/#contact" },
    availability: "Available for Q3 2026",
  },

  footer: {
    title: "Your org is ready. Are you?",
    description:
      "Available for Q3 2026 deployments. Engagements scoped to organizational complexity.\nDiscovery call is 30 minutes.",
    contactEmail: "contact@datachimplabs.com",
  },

  codeSimulation: [
    { text: "// Kanzi Brain — Persistent Memory Core\n", token: "comment" },
    { text: "async function ", token: "keyword" },
    { text: "initializeBrain", token: "function" },
    { text: "(orgId) {\n  ", token: "plain" },
    { text: "const", token: "keyword" },
    { text: " brain = await Kanzi.Memory.", token: "plain" },
    { text: "restore", token: "function" },
    { text: "({\n    org: orgId,\n    schema: ", token: "plain" },
    { text: '"healthcare-v2"', token: "plain" },
    { text: ",\n    private: ", token: "plain" },
    { text: "true", token: "keyword" },
    { text: "\n  });\n\n  ", token: "plain" },
    { text: "const", token: "keyword" },
    { text: " agents = await brain.", token: "plain" },
    { text: "swarm", token: "function" },
    { text: "([\n    ", token: "plain" },
    { text: '"revenue-cycle"', token: "plain" },
    { text: ",\n    ", token: "plain" },
    { text: '"operations"', token: "plain" },
    { text: ",\n    ", token: "plain" },
    { text: '"seo-growth"', token: "plain" },
    { text: "\n  ]);\n\n  ", token: "plain" },
    { text: "const", token: "keyword" },
    { text: " signal = await brain.", token: "plain" },
    { text: "analyze", token: "function" },
    { text: "({\n    period: ", token: "plain" },
    { text: '"30d"', token: "plain" },
    { text: ",\n    context: agents.outputs,\n    depth: ", token: "plain" },
    { text: '"cross-functional"', token: "plain" },
    { text: "\n  });\n\n  ", token: "plain" },
    { text: "if", token: "keyword" },
    { text: " (signal.hasActionable) {\n    ", token: "plain" },
    { text: "await", token: "keyword" },
    { text: " brain.", token: "plain" },
    { text: "dispatch", token: "function" },
    { text: "(signal.instructions);\n  }\n\n  ", token: "plain" },
    { text: "return", token: "keyword" },
    { text: " brain.", token: "plain" },
    { text: "accumulate", token: "function" },
    { text: "(signal.learnings);\n}", token: "plain" },
  ],
};
